import { paginatedQuery } from 'lib-shared-api-utils';

import { api } from 'app/app.api';

import {
  BuyGroupApiArg,
  BuyGroupApiResponse,
  BuyNumberApiArg,
  BuyNumberApiResponse,
  ReadAvailableNumbersApiArg,
  ReadAvailableNumbersApiResponse,
  ReadDefaultNumberApiArg,
  ReadDefaultNumberApiResponse,
  ReadNumbersApiArg,
  ReadNumbersApiResponse,
  ReadValidateNumberApiArg,
  ReadValidateNumberApiResponse,
  ReleaseNumberApiArg,
  UpdateNumberApiArg,
  UpdateNumberApiResponse,
} from './types/numbersApi.types';

export const numbersApi = api
  .enhanceEndpoints({ addTagTypes: ['Numbers', 'AvailableNumbers'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      readNumbers: builder.query<ReadNumbersApiResponse, ReadNumbersApiArg>({
        query: (arg) => {
          const queryConfig = paginatedQuery(
            arg,
            ({ capability, pbxRegion }) => ({
              version: 1,
              path: 'numbers',
              params: {
                sms_capable: capability === 'sms' ? 'true' : undefined,
                pbx_region: pbxRegion,
              },
            }),
            { pagination: { paramNaming: 'limitCount' } },
          );
          return queryConfig;
        },
        providesTags: [{ type: 'Numbers', id: 'LIST' }],
      }),
      readDefaultNumber: builder.query<
        ReadDefaultNumberApiResponse,
        ReadDefaultNumberApiArg | void
      >({
        query: (arg) => ({
          version: 1,
          path: 'numbers/default',
          params: arg?.withFallback ? { withFallback: 'true' } : undefined,
        }),
        providesTags: [{ type: 'Numbers', id: 'DEFAULT' }],
      }),
      readAvailableNumbers: builder.query<
        ReadAvailableNumbersApiResponse,
        ReadAvailableNumbersApiArg
      >({
        query: (arg) =>
          paginatedQuery(arg, ({ countryCode, numberType }) => ({
            version: 1,
            path: `available-numbers/${countryCode}`,
            params: {
              numbers_type: numberType,
            },
          })),
        providesTags: [{ type: 'AvailableNumbers', id: 'LIST' }],
      }),
      readValidateNumber: builder.query<
        ReadValidateNumberApiResponse,
        ReadValidateNumberApiArg
      >({
        query: ({ phoneNumber }) => ({
          version: 1,
          path: `numbers/validate/${phoneNumber}`,
        }),
      }),
      updateNumber: builder.mutation<
        UpdateNumberApiResponse,
        UpdateNumberApiArg
      >({
        query: ({ phoneNumber, data: body }) => ({
          version: 1,
          path: `numbers/${phoneNumber}`,
          method: 'PUT',
          body,
        }),
        invalidatesTags: (_result, error) =>
          error ? [] : [{ type: 'Numbers', id: 'LIST' }],
      }),
      buyNumber: builder.mutation<BuyNumberApiResponse, BuyNumberApiArg>({
        query: ({ number, name }) => ({
          version: 1,
          path: 'numbers',
          method: 'POST',
          body: {
            phone_number: number.phone_number,
            name,
          },
        }),
        invalidatesTags: (_result, error) =>
          error
            ? []
            : [
                { type: 'Numbers', id: 'LIST' },
                { type: 'AvailableNumbers', id: 'LIST' },
              ],
      }),
      buyGroup: builder.mutation<BuyGroupApiResponse, BuyGroupApiArg>({
        query: ({ group, name }) => ({
          version: 1,
          path: 'numbers',
          method: 'POST',
          body: {
            group: {
              group_id: group.group_id,
            },
            name,
          },
        }),
        invalidatesTags: (_result, error) =>
          error
            ? []
            : [
                { type: 'Numbers', id: 'LIST' },
                { type: 'AvailableNumbers', id: 'LIST' },
              ],
      }),
      releaseNumber: builder.mutation<void, ReleaseNumberApiArg>({
        query: ({ phoneNumber }) => ({
          version: 1,
          path: `numbers/${phoneNumber}`,
          method: 'DELETE',
        }),
        invalidatesTags: (_result, error) =>
          error
            ? []
            : [
                { type: 'Numbers', id: 'LIST' },
                { type: 'Numbers', id: 'DEFAULT' },
              ],
      }),
    }),
  });
